import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft } from 'lucide-react';
import { RootState, AppDispatch } from '../store';
import { submitParticipantPicks, clearSubmissionSuccess } from '../store/HallOfFameSlice';
import { SubmissionForm } from '../components/hall-of-fame/SubmissionForm';
import { SubmissionFormValues } from '../interface/HallOfFame';
import { toast } from 'react-toastify';
import LoaderHelper from '../utility/Loading/LoaderHelper';

export const HallOfFameSubmit: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error } = useSelector((state: RootState) => state.hallOfFame);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: SubmissionFormValues) => {
    setIsSubmitting(true);
    try {
      await dispatch(
        submitParticipantPicks({
          name: values.name,
          email: values.email,
          socialHandle: values.socialHandle || undefined, // Only send if provided
          experienceLevel: values.experienceLevel || undefined,
          tickers: [
            values.ticker1,
            values.ticker2,
            values.ticker3,
            values.ticker4,
            values.ticker5,
          ],
          prices: [
            values.price1,
            values.price2,
            values.price3,
            values.price4,
            values.price5,
          ],
        })
      ).unwrap();

      // Success! Show message and navigate to leaderboard
      toast.success('🔒 Your picks have been locked in! Welcome to the Hall of Fame!');
      dispatch(clearSubmissionSuccess());

      // Navigate to leaderboard after short delay
      setTimeout(() => {
        navigate('/hall-of-fame/leaderboard');
      }, 1500);
    } catch (err: any) {
      toast.error(err || 'Failed to submit picks');
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (loading) {
      LoaderHelper.loaderStatus(true);
    } else {
      LoaderHelper.loaderStatus(false);
    }
  }, [loading]);

  React.useEffect(() => {
    if (error) {
      toast.error(`Error: ${error}`);
    }
  }, [error]);

  return (
    <div className="min-h-screen bg-theme-primary py-12">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/hall-of-fame')}
          className="mb-6 flex items-center gap-2 text-theme-primary hover:text-blue-500 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Hall of Fame</span>
        </button>

        {/* Quick Info Banner */}
        <div className="mb-8 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl p-8 text-center shadow-xl">
          <h1 className="text-4xl font-bold mb-3">🏆 Submit Your 5 Stock Picks</h1>
          <p className="text-xl mb-4 opacity-95">
            Put your reputation on the line. Show everyone your best ideas.
          </p>
          <p className="text-base opacity-90 max-w-2xl mx-auto mb-2">
            Once you submit, your picks are permanent and public. You'll be ranked against other investors daily.
            Learn from the best. Build your track record. Prove you can beat the crowd.
          </p>
          <p className="text-sm opacity-80 max-w-2xl mx-auto">
            Only your name, picks, and performance are public. Your email remains private.
          </p>
        </div>

        {/* Submission Form */}
        <SubmissionForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
      </div>
    </div>
  );
};

export default HallOfFameSubmit;

